/**
 * Reiseorganisation im BK-Profil (Reiter „Betreuungen") — Übernahme aus
 * CRM 1.0 (ProfileCares.js, Fenster „N Reiseorganisationen").
 *
 * Liste mit Abfahrt, Abfahrtsadresse, Ankunft, Ankunftsadresse, Transferart;
 * „Vergangene Reisen anzeigen" blendet Reisen ein, deren Ankunft länger als
 * 12 h zurückliegt. Anlegen und Bearbeiten im Drawer (statt Modal), Löschen
 * aus dem Bearbeiten-Drawer mit Rückfrage. Die Abfahrtsadresse ist beim
 * Anlegen mit der Adresse der BK vorbelegt.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, EditDrawer } from '../../components/ui';
import { DateInput } from '../../components/DateInput';
import { TimeInput24 } from '../../components/DateTimeInput';
import { BRAND, getThemeColors } from '../../lib/theme';
import { formatDateTime } from '../../lib/dateFormat';
import type { V1User } from '../../lib/v1';
import {
  EMPTY_TRAVEL_INPUT, TRANSFER_TYPES, defaultDepartureAddress, travelToInput, v1Travels,
  type Travel, type TravelInput,
} from '../../lib/travels';
import { Th } from '../pflege/tabs/BetreuungenTab/BetreuungenTabHelpers';

export function TravelsCard({ user, dark }: { user: V1User; dark: boolean }) {
  const tc = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [rows, setRows] = useState<Travel[]>([]);
  const [total, setTotal] = useState(0);
  const [showOld, setShowOld] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Travel | 'new' | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    v1Travels.listForUser(user.id, showOld)
      .then(r => { setRows(r.rows); setTotal(r.total); setError(null); })
      .catch(() => setError(tr('betreuung.travels.load_error')))
      .finally(() => setLoading(false));
  }, [user.id, showOld, tr]);

  useEffect(() => { reload(); }, [reload]);

  const transferLabel = (v: string | null) => {
    const def = TRANSFER_TYPES.find(x => x.value === v);
    return def ? tr(def.labelKey) : (v || '—');
  };
  const cell: React.CSSProperties = { padding: '12px 16px', verticalAlign: 'top' };
  const multiline: React.CSSProperties = { ...cell, whiteSpace: 'pre-line' };

  return (
    <section data-testid="bk-travels-card">
    <Card dark={dark} style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '14px 16px', borderBottom: `1px solid ${tc.border}` }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: tc.heading }}>
          {tr('betreuung.travels.title', { count: total })}
        </h3>
        <Button dark={dark} variant="secondary" size="sm" onClick={() => setDraft('new')}>
          + {tr('betreuung.travels.create')}
        </Button>
      </div>

      {error ? (
        <div style={{ padding: 20, fontSize: 13, color: '#b42318' }}>{error}</div>
      ) : !loading && rows.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: tc.muted, fontSize: 13 }}>{tr('betreuung.travels.empty')}</div>
      ) : (
        <div style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 720 }}>
            <thead style={{ background: tc.tableHeader }}>
              <tr>
                <Th dark={dark}>{tr('betreuung.travels.th.abfahrt')}</Th>
                <Th dark={dark}>{tr('betreuung.travels.th.abfahrtsadresse')}</Th>
                <Th dark={dark}>{tr('betreuung.travels.th.ankunft')}</Th>
                <Th dark={dark}>{tr('betreuung.travels.th.ankunftsadresse')}</Th>
                <Th dark={dark}>{tr('betreuung.travels.th.transferart')}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr
                  key={r.id}
                  data-testid="bk-travel-row"
                  role="button"
                  tabIndex={0}
                  aria-label={tr('betreuung.travels.edit_aria', { date: formatDateTime(r.abfahrt) })}
                  onClick={() => setDraft(r)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDraft(r); } }}
                  style={{ borderTop: `1px solid ${tc.borderSoft}`, cursor: 'pointer' }}
                >
                  <td style={cell}><strong style={{ color: tc.heading }}>{formatDateTime(r.abfahrt)}</strong></td>
                  <td style={multiline}>{r.abfahrtsadresse || '—'}</td>
                  <td style={cell}>{formatDateTime(r.ankunft)}</td>
                  <td style={multiline}>{r.ankunftsadresse || '—'}</td>
                  <td style={cell}>{transferLabel(r.transferart)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderTop: `1px solid ${tc.borderSoft}`, fontSize: 12.5, color: tc.text, cursor: 'pointer' }}>
        <input type="checkbox" checked={showOld} onChange={e => setShowOld(e.target.checked)} />
        {tr('betreuung.travels.show_old')}
      </label>

      <EditDrawer
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={draft === 'new' ? tr('betreuung.travels.create') : tr('betreuung.travels.edit_title')}
        dark={dark}
      >
        {draft !== null && (
          <TravelForm
            dark={dark}
            userId={user.id}
            travel={draft === 'new' ? null : draft}
            initial={draft === 'new'
              ? { ...EMPTY_TRAVEL_INPUT, abfahrtsadresse: defaultDepartureAddress(user) }
              : travelToInput(draft)}
            onSaved={() => { setDraft(null); reload(); }}
            onCancel={() => setDraft(null)}
          />
        )}
      </EditDrawer>
    </Card>
    </section>
  );
}

export function TravelForm({ dark, userId, travel, initial, onSaved, onCancel }: {
  dark: boolean;
  userId: number;
  /** null = neue Reise. */
  travel: Travel | null;
  initial: TravelInput;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const tc = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [f, setF] = useState<TravelInput>(initial);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof TravelInput>(k: K, v: TravelInput[K]) => setF(prev => ({ ...prev, [k]: v }));

  const inputStyle: React.CSSProperties = {
    fontSize: 13, padding: '9px 11px', border: `1px solid ${tc.border}`, borderRadius: 8,
    background: tc.inputBg, color: tc.text, fontFamily: 'Outfit,sans-serif',
    width: '100%', boxSizing: 'border-box', outline: 'none',
  };
  const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: tc.muted, marginBottom: 6, display: 'block' };
  const row: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Die v1-API parst „Datum Uhrzeit" ohne Validierung — leere Daten würden
    // zum aktuellen Zeitpunkt. Deshalb Abfahrt und Ankunft hier verlangen.
    if (!f.abfahrtsdatum || !f.ankunftsdatum) { setError(tr('betreuung.travels.err.dates')); return; }
    const start = `${f.abfahrtsdatum} ${f.abfahrtszeit || '00:00'}`;
    const end = `${f.ankunftsdatum} ${f.ankunftszeit || '00:00'}`;
    if (end < start) { setError(tr('betreuung.travels.err.range')); return; }
    setSaving(true); setError(null);
    const payload: TravelInput = { ...f, abfahrtszeit: f.abfahrtszeit || '00:00', ankunftszeit: f.ankunftszeit || '00:00' };
    try {
      if (travel) await v1Travels.update(travel.id, payload);
      else await v1Travels.create(userId, payload);
      onSaved();
    } catch {
      setError(tr('betreuung.travels.err.save'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!travel) return;
    setSaving(true); setError(null);
    try { await v1Travels.delete(travel.id); onSaved(); }
    catch { setError(tr('betreuung.travels.err.delete')); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-testid="bk-travel-form">
      <div style={row}>
        <div>
          <label style={labelStyle}>{tr('betreuung.travels.field.abfahrtsdatum')} *</label>
          <DateInput ariaLabel={tr('betreuung.travels.field.abfahrtsdatum')} value={f.abfahrtsdatum} onChange={v => set('abfahrtsdatum', v)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>{tr('betreuung.travels.field.abfahrtszeit')}</label>
          <TimeInput24 ariaLabel={tr('betreuung.travels.field.abfahrtszeit')} value={f.abfahrtszeit} onChange={v => set('abfahrtszeit', v)} style={inputStyle} />
        </div>
      </div>
      <div>
        <label style={labelStyle} htmlFor="travel-abfahrtsadresse">{tr('betreuung.travels.field.abfahrtsadresse')}</label>
        <textarea id="travel-abfahrtsadresse" rows={4} value={f.abfahrtsadresse} onChange={e => set('abfahrtsadresse', e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
      </div>
      <div style={row}>
        <div>
          <label style={labelStyle}>{tr('betreuung.travels.field.ankunftsdatum')} *</label>
          <DateInput ariaLabel={tr('betreuung.travels.field.ankunftsdatum')} value={f.ankunftsdatum} min={f.abfahrtsdatum || undefined} onChange={v => set('ankunftsdatum', v)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>{tr('betreuung.travels.field.ankunftszeit')}</label>
          <TimeInput24 ariaLabel={tr('betreuung.travels.field.ankunftszeit')} value={f.ankunftszeit} onChange={v => set('ankunftszeit', v)} style={inputStyle} />
        </div>
      </div>
      <div>
        <label style={labelStyle} htmlFor="travel-ankunftsadresse">{tr('betreuung.travels.field.ankunftsadresse')}</label>
        <textarea id="travel-ankunftsadresse" rows={4} value={f.ankunftsadresse} onChange={e => set('ankunftsadresse', e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
      </div>
      <div>
        <label style={labelStyle} htmlFor="travel-transferart">{tr('betreuung.travels.field.transferart')}</label>
        <select id="travel-transferart" value={f.transferart} onChange={e => set('transferart', e.target.value)} style={inputStyle}>
          <option value="">{tr('betreuung.travels.field.transferart_ph')}</option>
          {TRANSFER_TYPES.map(o => <option key={o.value} value={o.value}>{tr(o.labelKey)}</option>)}
        </select>
      </div>
      <div style={row}>
        <div>
          <label style={labelStyle} htmlFor="travel-gesellschaft">{tr('betreuung.travels.field.transfergesellschaft')}</label>
          <input id="travel-gesellschaft" value={f.transfergesellschaft} onChange={e => set('transfergesellschaft', e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle} htmlFor="travel-transferid">{tr('betreuung.travels.field.transferid')}</label>
          <input id="travel-transferid" value={f.transferid} onChange={e => set('transferid', e.target.value)} style={inputStyle} />
        </div>
      </div>
      <div>
        <label style={labelStyle} htmlFor="travel-kommentar">{tr('betreuung.travels.field.kommentar')}</label>
        <textarea id="travel-kommentar" rows={3} value={f.kommentar} onChange={e => set('kommentar', e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
      </div>

      {error && <div style={{ fontSize: 12, color: '#b42318' }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
        {travel && (confirmDelete ? (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 12, color: tc.text }}>
            {tr('betreuung.travels.delete_confirm')}
            <button type="button" disabled={saving} onClick={remove} style={{ fontSize: 12, fontWeight: 600, background: '#b42318', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>{tr('common.action.delete')}</button>
            <button type="button" onClick={() => setConfirmDelete(false)} style={{ fontSize: 12, background: 'none', border: `1px solid ${tc.border}`, borderRadius: 6, padding: '6px 8px', color: tc.muted, cursor: 'pointer' }}>{tr('common.action.cancel')}</button>
          </span>
        ) : (
          <button type="button" onClick={() => setConfirmDelete(true)} style={{ fontSize: 12.5, background: 'none', border: 'none', color: '#b42318', cursor: 'pointer', padding: 0 }}>
            {tr('betreuung.travels.delete')}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <button type="button" onClick={onCancel} style={{ fontSize: 13, padding: '10px 18px', background: 'none', border: `1px solid ${tc.border}`, borderRadius: 8, color: tc.muted, cursor: 'pointer' }}>
          {tr('common.action.cancel')}
        </button>
        <button type="submit" disabled={saving} style={{ fontSize: 13, fontWeight: 600, padding: '10px 18px', background: BRAND, color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? '…' : tr('common.action.save')}
        </button>
      </div>
    </form>
  );
}
